import { useMemo } from 'react'

export type Loadable<D> = Loading | Loaded<D>

export type Loading = {
  loading: true
}
export type Loaded<D> = { loading: false; data: D }

type NotUndefined<T> = T extends undefined ? never : T

export const mkLoadable = <D extends NotUndefined<unknown>>(
  data: D | undefined,
): Loadable<D> =>
  data === undefined ? { loading: true } : { loading: false, data }

/**
 * Hook variant of mkLoadable that preserves referential equality over the
 * underlying data.
 */
export const useMkLoadable = <D extends NotUndefined<unknown>>(
  data: D | undefined,
) => {
  return useMemo(() => mkLoadable(data), [data])
}

/**
 * Transform a `Loadable<D>` into a `Loadable<X>` via a transform that maps from
 * `D -> X`. Conceptually similar to running `fmap` over Loadable<D> as a
 * functor.
 */
export const mapLoadable = <X, D>(
  loadable: Loadable<D>,
  fn: (data: D) => X,
): Loadable<X> => {
  if (loadable.loading) {
    return { loading: true }
  }
  return { loading: false, data: fn(loadable.data) }
}

type LoadedData<L extends Loadable<unknown>> =
  L extends Loaded<infer D> ? D : never

/**
 * Concatenate multiple `Loadable`s into a new `Loadable` that is the product of
 * the inputs, preserving the types of the underlying data.
 */
export const catLoadables = <I extends Record<string, Loadable<unknown>>>(
  ins: I,
): Loadable<{ [K in keyof I]: LoadedData<I[K]> }> => {
  const loading = Object.values(ins).some(loadable => loadable.loading)
  if (loading) {
    return { loading: true }
  } else {
    const data = Object.fromEntries(
      Object.entries(ins).map(([key, loadable]) => [
        key,
        (loadable as Loaded<unknown>).data,
      ]),
    ) as { [K in keyof I]: LoadedData<I[K]> }
    return {
      loading: false,
      data,
    }
  }
}

/** Returns Loadable `data` if loaded, or `undefined` if loading */
export const fromLoadable = <D extends NotUndefined<unknown>>(
  data: Loadable<D>,
): D | undefined => {
  return data.loading ? undefined : data.data
}

/**
 * Convenience function to check that a `Loadable<D>` (where `D` is a sum type
 * involving `null` or `undefined`) has loaded, and that the value represented
 * by `D` is neither `null` nor `undefined`
 */
export const isLoadedAndNonNullish = <D>(
  res: Loadable<D>,
): res is Loaded<Exclude<D, null | undefined>> => {
  return !res.loading && typeof res.data !== 'undefined' && res.data !== null
}

/**
 * If the Loadable has loaded, ensures that the result is non-nullish. Useful in
 * cases where we're hardcoding a lookup against a context that may return
 * `null`/`undefined`, and we want a runtime error if we've made a typo, or the
 * hardcoded lookup is otherwise not present in the result.
 */
export const errorIfLoadedButNullish = <D>(
  res: Loadable<D>,
): Loadable<Exclude<D, null | undefined>> => {
  if (!res.loading && (typeof res.data === 'undefined' || res.data === null)) {
    throw new Error(`Loadable returned nullish data`)
  }
  return res as Loadable<Exclude<D, null | undefined>>
}
